import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleMinus,
  faCircleCheck,
  faArrowDownShortWide,
  faArrowDownWideShort,
  faSearch,
} from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import {
  getChargingPointGlobalConfigurations,
  patchChargingPointGlobalConfigurations,
} from "../../api/ChargingPoint";
import { useAuth } from "../../providers/AuthContext";
import InputField from "../../components/InputField";
import Switch from "../../components/Switch";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import { getStatusChipStyleVars } from "../../helpers/StatusMapper";
import { getLabel } from "../../helpers/configurationLabels";

function isBoolString(value) {
  return (
    typeof value === "string" &&
    ["true", "false"].includes(value.toLowerCase())
  );
}

function formatValueForDisplay(value, t) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  if (isBoolString(value)) {
    return value.toLowerCase() === "true" ? t("common.yes") : t("common.no");
  }

  return String(value);
}

const ConfigurationsPage = () => {
  const { t } = useTranslation();
  const { getAccessToken } = useAuth();
  const [configurations, setConfigurations] = useState([]);
  const [filteredRequiredConfigurations, setFilteredRequiredConfigurations] =
    useState([]);
  const [filteredValueConfigurations, setFilteredValueConfigurations] =
    useState([]);
  const [filtereNoValueConfigurations, setFilteredNoValueConfigurations] =
    useState([]);
  const [configurationsToSave, setConfigurationsToSave] = useState([]);
  const [sortColumn, setSortColumn] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [search, setSearch] = useState("");
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: "",
    confirm: () => {},
  });
  const [configurationsLoaded, setConfigurationsLoaded] = useState(false);

  const [showOCPP, setShowOCPP] = useState(true);
  const [showValue, setShowValue] = useState(true);
  const [showNoValue, setShowNoValue] = useState(true);

  useEffect(() => {
    handleGetConfigurations();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, []);

  const applyFilterAndSort = (elements, setFilterState = () => {}) => {
    let filtered = elements;

    if (search) {
      const searchLower = search.toLowerCase();
      filtered = elements.filter((configuration) => {
        const label = getLabel(configuration.name).toLowerCase();
        return (
          configuration.defaultValue?.toLowerCase().includes(searchLower) ||
          configuration.newValue?.toLowerCase().includes(searchLower) ||
          configuration.name.toLowerCase().includes(searchLower) ||
          label.includes(searchLower)
        );
      });
    }

    const sorted = [...filtered].sort((a, b) => {
      let aValue = a[sortColumn]?.toString().toLowerCase() ?? "";
      let bValue = b[sortColumn]?.toString().toLowerCase() ?? "";

      if (sortColumn === "name") {
        aValue = getLabel(a.name).toLowerCase();
        bValue = getLabel(b.name).toLowerCase();
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      }
      return aValue < bValue ? 1 : -1;
    });

    setFilterState(sorted);
  };

  useEffect(() => {
    applyFilterAndSort(
      configurations?.requiredConfigurations || [],
      setFilteredRequiredConfigurations
    );
    applyFilterAndSort(
      configurations?.configurationsWithValues || [],
      setFilteredValueConfigurations
    );
    applyFilterAndSort(
      configurations?.configurationsWithoutValues || [],
      setFilteredNoValueConfigurations
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, [search, configurations, sortColumn, sortOrder]);

  const updateConfigList = (setConfigs) => {
    setConfigs((prevConfigs) => {
      const updated = [...prevConfigs];

      updated.forEach((conf) => {
        const existingIndex = configurationsToSave.findIndex(
          (c) => conf.configurationPublicId === c.configurationPublicId
        );

        if (existingIndex !== -1) {
          conf.newValue = configurationsToSave[existingIndex].newValue;
        } else {
          conf.newValue = undefined;
        }
      });
      return updated;
    });
  };

  useEffect(() => {
    updateConfigList(setFilteredRequiredConfigurations);
    updateConfigList(setFilteredValueConfigurations);
    updateConfigList(setFilteredNoValueConfigurations);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, [configurationsToSave]);

  const handleGetConfigurations = async () => {
    try {
      const accessToken = await getAccessToken();
      const newConfigurations = await getChargingPointGlobalConfigurations(
        accessToken
      );

      setConfigurations(newConfigurations);
      setFilteredRequiredConfigurations(
        newConfigurations?.requiredConfigurations
      );
      setFilteredValueConfigurations(
        newConfigurations?.configurationsWithValues
      );
      setFilteredNoValueConfigurations(
        newConfigurations?.configurationsWithoutValues
      );
      setConfigurationsLoaded(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn === column) {
      return sortOrder === "asc" ? faArrowDownShortWide : faArrowDownWideShort;
    }
    return null;
  };

  const handleUpdateConfiguration = (config, value) => {
    setConfigurationsToSave((prevConfigs) => {
      const existingIndex = prevConfigs.findIndex(
        (c) => c.configurationPublicId === config.configurationPublicId
      );

      if (existingIndex !== -1) {
        const updated = [...prevConfigs];
        updated[existingIndex] = {
          ...updated[existingIndex],
          newValue: value,
        };
        return updated;
      }
      return [...prevConfigs, { ...config, newValue: value }];
    });
  };

  const handleClearConfiguration = (configuration) => {
    setConfigurationsToSave((prevConfigs) =>
      prevConfigs.filter(
        (c) =>
          c.configurationPublicId !== configuration.configurationPublicId
      )
    );
  };

  const handleSaveConfigs = async () => {
    const toastId = toast.loading(t("configuration.savingGlobal"));

    try {
      const accessToken = await getAccessToken();

      const configs = {
        values: configurationsToSave.map((c) => ({
          configurationName: c.name,
          gitoDefaultValue: c.newValue,
        })),
      };

      setConfigurationsToSave([]);
      await patchChargingPointGlobalConfigurations(configs, accessToken);
      await handleGetConfigurations();

      toast.success(t("configuration.savedGlobal"), {
        id: toastId,
      });
    } catch (error) {
      toast.error(
        error.response?.data?.message || t("configuration.failedSaveGlobal"),
        {
          id: toastId,
        }
      );
    }
  };

  const renderSortableHeader = (label, column, className = "") => {
    const icon = getSortIcon(column);
    return (
      <th
        className={`border px-1.5 sm:px-2 h-9 text-left font-medium cursor-pointer select-none ${className}`}
        onClick={() => handleSort(column)}
      >
        <span className="inline-flex items-center gap-1">
          {label}
          {icon && <FontAwesomeIcon icon={icon} className="w-3.5 text-mute-500" />}
        </span>
      </th>
    );
  };

  const rowCellClass =
    "border px-1.5 sm:px-2 h-11 align-middle text-xs sm:text-sm min-w-0";

  const renderValueEditor = (configuration) => {
    const draft = configuration.newValue ?? "";
    const baseline = configuration.defaultValue ?? "";
    const treatAsBool = isBoolString(baseline) || isBoolString(draft);

    if (treatAsBool) {
      const checked =
        (draft || baseline || "false").toLowerCase() === "true";
      return (
        <div className="flex h-8 items-center">
          <Switch
            checked={checked}
            onChange={(isChecked) =>
              handleUpdateConfiguration(
                configuration,
                isChecked ? "true" : "false"
              )
            }
          />
        </div>
      );
    }

    return (
      <input
        type="text"
        className="input-default-sm w-full !h-8 !py-0 text-xs sm:text-sm leading-none"
        value={draft}
        placeholder={t("configuration.newValuePlaceholder")}
        onChange={(e) =>
          handleUpdateConfiguration(configuration, e.target?.value)
        }
      />
    );
  };

  const renderConfigTable = (title, rows) => {
    if (!rows?.length) {
      return null;
    }

    return (
      <>
        <div className="flex w-full gap-2 border-b-4 border-b-mute-200 pt-4 mb-4">
          <h5 className="text-sm font-bold text-mute-900">{title}</h5>
          <span className="text-xs text-mute-500 self-end pb-0.5">
            ({rows.length})
          </span>
        </div>
        <div className="overflow-x-auto mb-6">
          <table className="w-full border text-xs sm:text-sm table-fixed">
            <thead className="bg-mute-100">
              <tr>
                {renderSortableHeader(t("common.setting"), "name", "min-w-0")}
                {renderSortableHeader(
                  t("common.current"),
                  "defaultValue",
                  "hidden sm:table-cell min-w-0"
                )}
                {renderSortableHeader(t("common.newValue"), "newValue", "min-w-0")}
              </tr>
            </thead>
            <tbody>
              {rows.map((configuration) => {
                const isDirty =
                  configuration.newValue !== undefined &&
                  configuration.newValue !== null &&
                  String(configuration.newValue) !== "";
                const label = getLabel(configuration.name);
                const currentDisplay = formatValueForDisplay(
                  configuration.defaultValue,
                  t
                );

                return (
                  <tr
                    key={configuration.configurationPublicId || configuration.name}
                    className={
                      isDirty
                        ? "bg-warning-100 ring-2 ring-inset ring-amber-300"
                        : ""
                    }
                  >
                    <td
                      className={`${rowCellClass} font-semibold truncate`}
                      title={label}
                    >
                      {label}
                    </td>
                    <td
                      className={`${rowCellClass} hidden sm:table-cell truncate`}
                      title={currentDisplay}
                    >
                      {currentDisplay}
                    </td>
                    <td className={rowCellClass}>
                      {renderValueEditor(configuration)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  const sectionFilters = [
    {
      key: "ocpp",
      label: t("configuration.ocppRequired"),
      active: showOCPP,
      onToggle: () => setShowOCPP(!showOCPP),
      count: filteredRequiredConfigurations.length,
      styleKey: "PERIODIC",
    },
    {
      key: "value",
      label: t("configuration.hasValue"),
      active: showValue,
      onToggle: () => setShowValue(!showValue),
      count: filteredValueConfigurations.length,
      styleKey: "CHARGING",
    },
    {
      key: "novalue",
      label: t("configuration.noValue"),
      active: showNoValue,
      onToggle: () => setShowNoValue(!showNoValue),
      count: filtereNoValueConfigurations.length,
      styleKey: "OTHER",
    },
  ];

  return (
    <>
      <HeaderBar />
      <div className="flex flex-col">
        <div className="flex flex-row">
          <Sidebar />
          <main className="page">
            <div className="absolute inset-0 pointer-events-none">
              <LoadingTablePage
                title={t("pages.configurations")}
                loading={!configurationsLoaded}
              />
            </div>

            <div
              className={`${
                !configurationsLoaded ? "loading" : ""
              } page-content`}
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2>{t("pages.configurations")}</h2>
                  <p className="text-sm text-mute-500 mt-1">
                    {t("configuration.description")}
                  </p>
                </div>
                <button
                  type="button"
                  className="btn btn-primary shrink-0"
                  disabled={configurationsToSave.length === 0}
                  onClick={handleSaveConfigs}
                >
                  {t("common.save")}
                  {configurationsToSave.length > 0
                    ? ` (${configurationsToSave.length})`
                    : ""}
                </button>
              </div>

              <div className="surface-card p-4 sm:p-6 w-full">
                <div className="mb-4 flex w-full flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-end lg:justify-between">
                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                    {sectionFilters.map((filter) => (
                      <button
                        key={filter.key}
                        type="button"
                        onClick={filter.onToggle}
                        className={`chip ${filter.active ? "chip--active" : ""} shadow-md`}
                        style={getStatusChipStyleVars(filter.styleKey)}
                      >
                        <FontAwesomeIcon
                          icon={faCircleCheck}
                          className="chip__check text-xs"
                        />
                        <span>{filter.label}</span>
                        <span className="chip__count">{filter.count}</span>
                      </button>
                    ))}
                  </div>
                  <div className="w-full shrink-0 lg:w-72">
                    <InputField
                      hideLabel
                      icon={faSearch}
                      placeholder={t("configuration.searchPlaceholder")}
                      value={search}
                      onChange={(v) => setSearch(v.target.value)}
                    />
                  </div>
                </div>

                {configurationsToSave.length > 0 && (
                  <>
                    <div className="flex w-full gap-2 border-b-4 border-b-kraft-300 pt-2 mb-4">
                      <h5 className="text-sm font-bold text-mute-900">
                        {t("common.pendingChanges")}
                      </h5>
                      <span className="text-xs text-mute-500 self-end pb-0.5">
                        ({configurationsToSave.length})
                      </span>
                    </div>
                    <div className="overflow-x-auto mb-6">
                      <table className="w-full border text-xs sm:text-sm table-fixed">
                        <thead className="bg-warning-100">
                          <tr>
                            <th className="border px-1.5 sm:px-2 h-9 text-left font-medium min-w-0">
                              {t("common.setting")}
                            </th>
                            <th className="border px-1.5 sm:px-2 h-9 text-left font-medium hidden sm:table-cell min-w-0">
                              {t("common.current")}
                            </th>
                            <th className="border px-1.5 sm:px-2 h-9 text-left font-medium min-w-0">
                              {t("common.newValue")}
                            </th>
                            <th className="border w-12 sm:w-14 h-9 px-1 text-center font-medium whitespace-nowrap">
                              <span className="sr-only">{t("common.remove")}</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {configurationsToSave.map((configuration) => {
                            const label = getLabel(configuration.name);
                            const currentDisplay = formatValueForDisplay(
                              configuration.defaultValue,
                              t
                            );

                            return (
                              <tr
                                key={
                                  configuration.configurationPublicId ||
                                  configuration.name
                                }
                                className="bg-warning-100 ring-2 ring-inset ring-amber-300"
                              >
                                <td
                                  className={`${rowCellClass} font-semibold truncate`}
                                  title={label}
                                >
                                  {label}
                                </td>
                                <td
                                  className={`${rowCellClass} hidden sm:table-cell truncate`}
                                  title={currentDisplay}
                                >
                                  {currentDisplay}
                                </td>
                                <td className={rowCellClass}>
                                  {renderValueEditor(configuration)}
                                </td>
                                <td className={`${rowCellClass} w-12 sm:w-14 whitespace-nowrap`}>
                                  <div className="flex h-8 w-full items-center justify-center">
                                    <button
                                      type="button"
                                      className="inline-flex h-8 w-8 items-center justify-center rounded text-error-500 hover:bg-error-50"
                                      title={t("configuration.removePending")}
                                      aria-label={t("configuration.removePendingFor", { label })}
                                      onClick={() =>
                                        handleClearConfiguration(configuration)
                                      }
                                    >
                                      <FontAwesomeIcon
                                        icon={faCircleMinus}
                                        className="w-4 h-4"
                                      />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {showOCPP &&
                  renderConfigTable(
                    t("configuration.ocppRequired"),
                    filteredRequiredConfigurations
                  )}
                {showValue &&
                  renderConfigTable(
                    t("configuration.hasValue"),
                    filteredValueConfigurations
                  )}
                {showNoValue &&
                  renderConfigTable(
                    t("configuration.noValue"),
                    filtereNoValueConfigurations
                  )}
              </div>
            </div>
          </main>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        onClose={() => {
          setConfirmState((s) => ({ ...s, isOpen: false }));
        }}
        onConfirm={() => {
          setConfirmState((s) => ({ ...s, isOpen: false }));
          confirmState.confirm();
        }}
      />
    </>
  );
};

export default ConfigurationsPage;
